import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Badge } from "./ui/badge";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "./ui/empty";
import { Skeleton } from "./ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "./ui/toggle-group";
import { AlertTriangle, Activity } from "lucide-react";
import { formatDuration, formatWhen, humanize } from "../pages/ActivityShared";
import {
  getScreenTimeline,
  type TimelineDomain,
} from "../api/domains/activity";
import { buildActivityLink } from "../pages/activityLinks";

type TimelineEntry = {
  id: string;
  timestamp: string;
  domain: string;
  kind: string;
  severity: string;
  title: string;
  description?: string;
  endedAt?: string;
  durationMs?: number;
  result?: string;
  linkType?: string;
  linkId?: string;
};

type ScreenTimeline = {
  range: { from: string; to: string };
  status: {
    currentPresentation?: string;
    currentItem?: string;
    currentIncident?: string;
    currentIncidentId?: string;
    lastHealthyPlayback?: string;
    lastManifestActivation?: string;
    lastHeartbeatAt?: string;
    playerVersion?: string;
    health: string;
    healthReason: string;
  };
  entries: TimelineEntry[];
};

/**
 * The domains a reader would filter by. These are the Screen Events categories
 * plus the two derived sources — state intervals and incidents — that have no
 * event of their own but belong in the same history.
 */
// Domain and range structures hold translation keys, never rendered text.
// Labels are resolved with t() at render so the timeline follows language
// changes.
const domains = [
  { value: "", labelKey: "timeline.domains.all" },
  { value: "playback", labelKey: "timeline.domains.playback" },
  { value: "connectivity", labelKey: "timeline.domains.connectivity" },
  { value: "reliability", labelKey: "timeline.domains.reliability" },
  { value: "scheduling", labelKey: "timeline.domains.scheduling" },
  { value: "manifest", labelKey: "timeline.domains.manifest" },
  { value: "commands", labelKey: "timeline.domains.commands" },
  { value: "updates", labelKey: "timeline.domains.updates" },
  { value: "takeovers", labelKey: "timeline.domains.takeovers" },
  { value: "state", labelKey: "timeline.domains.state" },
  { value: "incidents", labelKey: "timeline.domains.incidents" },
  { value: "audit", labelKey: "timeline.domains.admin" },
] as const;

const ranges = [
  { value: "24h", labelKey: "uptime.windows.24h" },
  { value: "7d", labelKey: "uptime.windows.7d" },
  { value: "30d", labelKey: "uptime.windows.30d" },
] as const;

/** Reported periods in milliseconds; the server windows on from/to. */
const rangeWindowsMs = {
  "24h": 86_400_000,
  "7d": 604_800_000,
  "30d": 2_592_000_000,
} as const;
type RangeKey = keyof typeof rangeWindowsMs;

function formatOptional(value: string | undefined, notReported: string) {
  return value ? formatWhen(value) : notReported;
}

/**
 * The main diagnostic view for one screen: everything that happened to it, in
 * one order. The compact proof and event columns beside it remain a summary.
 */
export function ScreenTimeline({ screenId }: { screenId: string }) {
  const { t } = useTranslation("activity");
  const notReported = t("screenActivity.notReported");
  const [domain, setDomain] = useState<TimelineDomain | "">("");
  const [range, setRange] = useState<RangeKey>("24h");
  const query = useQuery({
    queryKey: ["activity", "screen-timeline", screenId, domain, range],
    queryFn: () => {
      const to = new Date();
      const from = new Date(to.getTime() - rangeWindowsMs[range]);
      return getScreenTimeline(screenId, {
        from: from.toISOString(),
        to: to.toISOString(),
        ...(domain ? { domain } : {}),
      });
    },
    refetchInterval: 30_000,
  });

  return (
    <section
      className="min-w-0 space-y-4 rounded-xl border border-border bg-card p-4"
      aria-labelledby="screen-timeline-title"
    >
      <header className="flex min-w-0 flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 id="screen-timeline-title" className="text-sm font-semibold">
            {t("timeline.title")}
          </h3>
          <p className="mt-1 max-w-[62ch] text-sm text-muted-foreground">
            {t("timeline.description")}
          </p>
        </div>
        <ToggleGroup
          aria-label={t("timeline.rangeLabel")}
          value={[range]}
          onValueChange={(values) => {
            const next = ranges.find((item) => item.value === values[0]);
            if (next) setRange(next.value);
          }}
          multiple={false}
          variant="outline"
          size="sm"
          spacing={1}
          className="flex-wrap"
        >
          {ranges.map((item) => (
            <ToggleGroupItem key={item.value} value={item.value}>
              {t(item.labelKey)}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </header>

      {query.data?.status && (
        <CurrentStatus status={query.data.status} notReported={notReported} />
      )}

      <ToggleGroup
        aria-label={t("timeline.domainLabel")}
        value={[domain || "__all__"]}
        onValueChange={(values) => {
          const selected = values[0] === "__all__" ? "" : values[0];
          const next = domains.find((item) => item.value === selected);
          if (next) setDomain(next.value);
        }}
        multiple={false}
        variant="outline"
        size="sm"
        spacing={1}
        className="flex-wrap"
      >
        {domains.map((item) => (
          <ToggleGroupItem
            key={item.value || "__all__"}
            value={item.value || "__all__"}
          >
            {t(item.labelKey)}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>

      {query.isLoading && (
        <div className="space-y-2" aria-label={t("timeline.loading")}>
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      )}
      {query.error && (
        <Alert variant="destructive">
          <AlertTriangle aria-hidden="true" />
          <AlertTitle>{t("timeline.loadFailed")}</AlertTitle>
          <AlertDescription>{query.error.message}</AlertDescription>
        </Alert>
      )}
      {query.data &&
        // Go marshals an empty slice as null, so the collection is defaulted
        // rather than indexed into blindly.
        ((query.data.entries ?? []).length === 0 ? (
          <Empty className="min-h-36 border-dashed p-5">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Activity aria-hidden="true" />
              </EmptyMedia>
              <EmptyTitle role="heading" aria-level={4}>
                {domain ? t("timeline.emptyDomain") : t("timeline.emptyNone")}
              </EmptyTitle>
              <EmptyDescription>{t("timeline.emptyHint")}</EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <ol className="divide-y divide-border">
            {(query.data.entries ?? []).map((entry) => (
              <li
                key={entry.id}
                className={`grid min-w-0 gap-x-3 gap-y-1 border-l-2 py-3 pl-3 sm:grid-cols-[8rem_7rem_minmax(0,1fr)] ${entry.severity === "critical" || entry.severity === "error" ? "border-l-destructive" : entry.severity === "warning" ? "border-l-amber-500" : "border-l-transparent"}`}
              >
                <time
                  className="text-xs text-muted-foreground"
                  dateTime={entry.timestamp}
                >
                  {formatWhen(entry.timestamp)}
                </time>
                <Badge variant="secondary" className="justify-self-start">
                  {humanize(entry.domain)}
                </Badge>
                <div className="grid min-w-0 gap-1">
                  <strong className="text-sm font-medium">{entry.title}</strong>
                  {entry.description && (
                    <p className="break-words text-sm text-muted-foreground">
                      {entry.description}
                    </p>
                  )}
                  <span className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    {entry.durationMs != null && (
                      <span>{formatDuration(entry.durationMs)}</span>
                    )}
                    {/* An interval with no end is still running, which is a
                        different statement from one that ended. */}
                    {(entry.kind === "interval" || entry.kind === "session") &&
                      entry.endedAt === undefined &&
                      entry.durationMs == null && (
                        <span>{t("timeline.stillOpen")}</span>
                      )}
                    {entry.result && <span>{humanize(entry.result)}</span>}
                    {entry.linkType === "incident" ? (
                      <Link
                        className="underline underline-offset-4"
                        to={buildActivityLink("incidents")}
                      >
                        {t("timeline.viewIncident")}
                      </Link>
                    ) : (
                      entry.linkId && (
                        <TimelineResourceLink
                          type={entry.linkType}
                          id={entry.linkId}
                        />
                      )
                    )}
                  </span>
                </div>
              </li>
            ))}
          </ol>
        ))}
    </section>
  );
}

function CurrentStatus({
  status,
  notReported,
}: {
  status: ScreenTimeline["status"];
  notReported: string;
}) {
  const { t } = useTranslation("activity");
  return (
    <dl className="grid gap-3 rounded-xl border border-border bg-muted/30 p-3 sm:grid-cols-2 xl:grid-cols-4">
      <div>
        <dt className="text-xs text-muted-foreground">
          {t("timeline.facts.health")}
        </dt>
        <dd className="mt-1 grid gap-1 text-sm">
          <Badge
            variant={
              status.health === "offline"
                ? "destructive"
                : status.health === "impaired"
                  ? "secondary"
                  : "outline"
            }
            className="justify-self-start"
          >
            {humanize(status.health)}
          </Badge>
          {/* The reason is shown beside the classification so it is never an
              unexplained label. */}
          <small>{humanize(status.healthReason)}</small>
        </dd>
      </div>
      <div>
        <dt className="text-xs text-muted-foreground">
          {t("screenActivity.facts.presentation")}
        </dt>
        <dd className="mt-1 break-words text-sm">
          {status.currentPresentation || notReported}
        </dd>
      </div>
      <div>
        <dt className="text-xs text-muted-foreground">
          {t("timeline.facts.item")}
        </dt>
        <dd className="mt-1 break-words text-sm">
          {status.currentItem || notReported}
        </dd>
      </div>
      <div>
        <dt className="text-xs text-muted-foreground">
          {t("timeline.facts.incident")}
        </dt>
        <dd className="mt-1 break-words text-sm">
          {status.currentIncident ? (
            <Link
              className="underline underline-offset-4"
              to={buildActivityLink("incidents")}
            >
              {status.currentIncident}
            </Link>
          ) : (
            t("shared.none")
          )}
        </dd>
      </div>
      <div>
        <dt className="text-xs text-muted-foreground">
          {t("screenActivity.facts.lastPlayback")}
        </dt>
        <dd className="mt-1 text-sm">
          {formatOptional(status.lastHealthyPlayback, notReported)}
        </dd>
      </div>
      <div>
        <dt className="text-xs text-muted-foreground">
          {t("screenActivity.facts.lastActivation")}
        </dt>
        <dd className="mt-1 text-sm">
          {formatOptional(status.lastManifestActivation, notReported)}
        </dd>
      </div>
      <div>
        <dt className="text-xs text-muted-foreground">
          {t("timeline.facts.heartbeat")}
        </dt>
        <dd className="mt-1 text-sm">
          {formatOptional(status.lastHeartbeatAt, notReported)}
        </dd>
      </div>
      <div>
        <dt className="text-xs text-muted-foreground">
          {t("timeline.facts.version")}
        </dt>
        <dd className="mt-1 text-sm">{status.playerVersion || notReported}</dd>
      </div>
    </dl>
  );
}

function TimelineResourceLink({ type, id }: { type?: string; id: string }) {
  const { t } = useTranslation("activity");
  const open = t("timeline.openResource");
  const path =
    type === "screen"
      ? `/screens/${id}`
      : type === "playlist"
        ? `/playlists/${id}`
        : type === "layout"
          ? `/layouts/${id}`
          : type === "schedule"
            ? `/schedules/${id}`
            : ["asset", "media", "widget", "source"].includes(type ?? "")
              ? `/assets?search=${encodeURIComponent(id)}`
              : type === "user"
                ? "/settings/users"
                : undefined;
  return path ? (
    <Link className="underline underline-offset-4" to={path}>
      {open}
    </Link>
  ) : (
    open
  );
}
